import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatCents } from '@pirouette/shared';
import { useAllFamilyBalances } from '@/data/billing';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Empty } from '@/components/ui/Empty';
import { Badge } from '@/components/ui/Badge';

export const Route = createFileRoute('/admin/billing/')({
  component: BillingIndex,
});

interface FamilyRef {
  id: string;
  primary_guardian_name: string;
  billing_email: string;
}

function useFamilyRefs(ids: string[]) {
  return useQuery({
    enabled: ids.length > 0,
    queryKey: ['family-refs', [...ids].sort().join(',')],
    queryFn: async (): Promise<FamilyRef[]> => {
      const { data, error } = await supabase
        .from('families')
        .select('id, primary_guardian_name, billing_email')
        .in('id', ids);
      if (error) throw error;
      return data ?? [];
    },
  });
}

function BillingIndex() {
  const balances = useAllFamilyBalances();
  const familyIds = (balances.data ?? []).map((b) => b.family_id);
  const families = useFamilyRefs(familyIds);
  const familyById = new Map((families.data ?? []).map((f) => [f.id, f]));

  const outstanding =
    balances.data
      ?.filter((b) => b.outstanding_cents > 0)
      .sort((a, b) => b.outstanding_cents - a.outstanding_cents) ?? [];

  const totalOutstanding = outstanding.reduce((s, b) => s + b.outstanding_cents, 0);
  const totalOverdue = outstanding.filter((b) => b.overdue_count > 0).length;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
            Money
          </p>
          <h1 className="mt-2">Billing</h1>
          {balances.data && (
            <p className="mt-1 text-[var(--color-ink-muted)]">
              {formatCents(totalOutstanding)} outstanding across {outstanding.length} families
              {totalOverdue > 0 && (
                <>
                  {' · '}
                  <span className="text-[var(--color-danger)]">{totalOverdue} overdue</span>
                </>
              )}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/billing/subscriptions">
            <Button variant="secondary">Subscriptions</Button>
          </Link>
          <Link to="/admin/billing/plans">
            <Button variant="secondary">Plans</Button>
          </Link>
          <Link to="/admin/billing/late-fees">
            <Button variant="secondary">Late fees</Button>
          </Link>
          <Link to="/admin/billing/export">
            <Button variant="secondary">Export CSV</Button>
          </Link>
          <Link to="/admin/billing/new">
            <Button>New charge</Button>
          </Link>
        </div>
      </div>

      {balances.isLoading && <p aria-live="polite">Loading…</p>}

      {outstanding.length === 0 && !balances.isLoading && (
        <Empty
          title="All paid up"
          description="No families currently have outstanding balances."
          action={
            <Link to="/admin/billing/new">
              <Button>Add a charge</Button>
            </Link>
          }
        />
      )}

      {outstanding.length > 0 && (
        <div role="region" aria-label="Outstanding balances" className="surface overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-[var(--color-surface-sunken)] text-[var(--text-sm)]">
              <tr>
                <th scope="col" className="px-4 py-3 font-medium">
                  Family
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Email
                </th>
                <th scope="col" className="px-4 py-3 text-right font-medium">
                  Outstanding
                </th>
                <th scope="col" className="px-4 py-3 text-right font-medium">
                  Open
                </th>
                <th scope="col" className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {outstanding.map((b) => {
                const fam = familyById.get(b.family_id);
                return (
                  <tr key={b.family_id} className="border-t border-[var(--color-border)]">
                    <td className="px-4 py-3 font-medium">{fam?.primary_guardian_name ?? '…'}</td>
                    <td className="px-4 py-3 text-[var(--color-ink-muted)]">
                      {fam?.billing_email ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums">
                      {formatCents(b.outstanding_cents)}
                    </td>
                    <td className="px-4 py-3 text-right text-[var(--color-ink-muted)] tabular-nums">
                      {b.open_count}
                      {b.overdue_count > 0 && (
                        <Badge tone="danger" className="ml-2">
                          {b.overdue_count} overdue
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to="/admin/billing/$familyId"
                        params={{ familyId: b.family_id }}
                        className="text-[var(--color-accent)] underline-offset-2 hover:underline"
                      >
                        Open
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
